/**
 * TAKE (PLAN §5.3, D3): the operator's most consequential click, in the
 * on-air colour, big. Its states:
 *
 *   ready     the next part can be taken
 *   pending   a take is in flight: disabled until its ack or nack — a second
 *             press cannot send a second take
 *   blocked   nothing to take (not active, no next), too soon after the last
 *             take (`minimumTakeSpan`), or not permitted — disabled, with why
 *   refused   the last take was nacked: enabled again, the reason shown
 *
 * The hotkeys (F12, NumpadEnter…) are the view's; the button shows them.
 */
import { Button } from '../ui/button.js';

export type TakeButtonState =
  | { readonly kind: 'ready' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'blocked'; readonly reason: string }
  | { readonly kind: 'refused'; readonly reason: string };

export type TakeButtonProps = {
  readonly state: TakeButtonState;
  /** What a take puts on air; null when nothing is next. */
  readonly nextTitle: string | null;
  /** The keys that take, as shown (`F12`). */
  readonly hotkeys: readonly string[];
  readonly onTake: () => void;
};

function detail(state: TakeButtonState, nextTitle: string | null): string {
  switch (state.kind) {
    case 'ready':
      return nextTitle === null ? '' : `Next: ${nextTitle}`;
    case 'pending':
      return 'Taking…';
    case 'blocked':
      return state.reason;
    case 'refused':
      return `Refused: ${state.reason}`;
  }
}

export function TakeButton({ state, nextTitle, hotkeys, onTake }: TakeButtonProps) {
  const enabled = state.kind === 'ready' || state.kind === 'refused';
  return (
    <div className="grid gap-1.5" data-testid="take" data-state={state.kind}>
      <Button
        variant="take"
        className="h-20 w-full text-2xl"
        disabled={!enabled}
        aria-busy={state.kind === 'pending'}
        aria-keyshortcuts={hotkeys.join(' ')}
        onClick={onTake}
        data-testid="take-button"
      >
        {state.kind === 'pending' ? 'Taking…' : 'Take'}
      </Button>
      <p className={state.kind === 'refused' ? 'text-sm font-medium text-destructive' : 'text-sm text-muted-foreground'} role={state.kind === 'refused' ? 'alert' : undefined} data-testid="take-detail">
        {detail(state, nextTitle)}
        {hotkeys.length > 0 ? <span className="ml-2 text-xs text-muted-foreground">({hotkeys.join(', ')})</span> : null}
      </p>
    </div>
  );
}
